import React, { useEffect, useMemo, useState } from 'react';
import { useAdyenCheckout } from 'components/commercetools-ui/organisms/checkout/provider';
import { PaymentState, PaymentType } from 'components/commercetools-ui/organisms/checkout/provider/payment/types';
import V12Image from 'components/icons/v12-finance';
import { CurrencyHelpers } from 'helpers/currencyHelpers';
import { useFormat } from 'helpers/hooks/useFormat';
import useLocale from 'helpers/hooks/useLocale';
import { useCart } from 'frontastic';
import Preview from '../wrapper';

const PaymentPreview = () => {
  const { selectedPaymentMethod, adyenCheckoutInstanceState, v12PaymentData } = useAdyenCheckout();
  const [paymentState, setPaymentState] = useState<PaymentState>();
  const { data: cartData } = useCart();
  const { locale } = useLocale();
  const [type, setType] = useState<string>();
  const { formatMessage: formatCheckoutMessage } = useFormat({ name: 'checkout' });

  const areAllGivexPayments = useMemo(() => {
    return cartData?.sum?.centAmount === 0 && cartData?.payments && cartData?.payments?.length > 0;
  }, [cartData?.sum, cartData?.payments]);

  const containSplitGivexPayments = useMemo(() => {
    const hasGivexPayments =
      cartData?.payments && cartData?.payments.some((payment) => payment.paymentMethod.toLowerCase() === 'givex');
    return cartData?.sum?.centAmount && cartData?.sum?.centAmount > 0 && hasGivexPayments;
  }, [cartData?.sum, cartData?.payments]);

  useEffect(() => {
    if (adyenCheckoutInstanceState) setPaymentState(adyenCheckoutInstanceState);
  }, [adyenCheckoutInstanceState]);

  useEffect(() => {
    if (selectedPaymentMethod) {
      setType(selectedPaymentMethod);
    } else if (areAllGivexPayments) {
      setType('giftcard');
    }
  }, [selectedPaymentMethod]);

  const getTypeName = (type: string) => {
    if (type === 'bcmc') {
      return 'Bancontact card';
    } else if (type === 'klarna') {
      return 'Pay later with Klarna';
    } else if (type === 'klarna_account') {
      return 'Pay over time with Klarna';
    } else if (type === 'klarna_paynow') {
      return 'Pay now with Klarna';
    } else {
      return type;
    }
  };

  const getPaymentDetails = () => {
    if (type === 'scheme') {
      return {
        label: (
          <>
            <div>
              <span className="mr-10 text-14 font-light">Payment Method:</span>
              <span className="text-14">Credit/Debit Card</span>
            </div>
            <div>
              <span className="mr-10 text-14 font-light">Cardholder:</span>
              <span className="text-14">{paymentState?.data.paymentMethod.holderName}</span>
            </div>
          </>
        ),
        content: (
          <div className="flex items-center gap-4 md:gap-18">
            <img
              className="h-[24px]"
              src={`https://checkoutshopper-live.adyen.com/checkoutshopper/images/logos/${
                paymentState?.data?.paymentMethod?.brand ?? 'card'
              }.svg`}
              alt={type}
            />
          </div>
        ),
      };
    } else if (
      type === 'ideal' ||
      type === 'paypal' ||
      type === 'bcmc' ||
      type === 'clearpay' ||
      type?.indexOf('klarna') !== -1
    ) {
      return {
        label: (
          <>
            <div>
              <span className="mr-10 text-14 font-light">Payment Method:</span>
              <span className="text-14 capitalize">{getTypeName(type ?? 'Other')}</span>
            </div>
          </>
        ),
        content: (
          <div className="flex items-center gap-4 md:gap-18">
            <img
              className="h-[24px]"
              src={`https://checkoutshopper-live.adyen.com/checkoutshopper/images/logos/${type}.svg`}
              alt={type}
            />
          </div>
        ),
      };
    } else if (type === PaymentType.V12) {
      return {
        label: (
          <>
            <div>
              <span className="mr-10 text-14 font-light">Payment Method:</span>
              <span className="text-14">V12 Retail Finance</span>
            </div>
          </>
        ),
        content: (
          <div className="flex items-center gap-4 md:gap-18">
            <V12Image />
          </div>
        ),
      };
    } else if (type === 'giftcard') {
      return {
        label: (
          <>
            <div>
              <span className="mr-10 text-14 font-light">
                {formatCheckoutMessage({ id: 'payment.method', defaultMessage: 'Payment Method' })}:
              </span>
              <span className="text-14">{formatCheckoutMessage({ id: 'gift.card', defaultMessage: 'Gift Card' })}</span>
            </div>
            <div>
              <span className="mr-10 text-14 font-light">
                {formatCheckoutMessage({ id: 'applied.giftcards', defaultMessage: 'Applied Gift Cards' })}:
              </span>
            </div>
            {cartData?.payments?.map(({ giftcardNumber, amountPlanned }, index) => (
              <div key={'applied-card-' + index} className="my-10 rounded bg-[#f7f8f9] px-14 py-7">
                <div className="w-full">
                  <p className="text-14 font-light">{giftcardNumber}</p>
                  <p className="relative mt-8 text-14 font-medium">
                    {amountPlanned && CurrencyHelpers.formatForCurrency(amountPlanned, locale)}
                  </p>
                </div>
              </div>
            ))}
          </>
        ),
      };
    } else {
      return {
        label: 'Method',
        content: <div className="flex items-center gap-4 md:gap-18">Other</div>,
      };
    }
  };

  const getAdditionalPaymentDetails = () => {
    if (containSplitGivexPayments) {
      return (
        <>
          <div>
            <span className="mr-10 text-14 font-light">
              {formatCheckoutMessage({ id: 'payment.method', defaultMessage: 'Payment Method' })}:
            </span>
            <span className="text-14">{formatCheckoutMessage({ id: 'gift.card', defaultMessage: 'Gift Card' })}</span>
          </div>
          <div>
            <span className="mr-10 text-14 font-light">
              {formatCheckoutMessage({ id: 'applied.giftcards', defaultMessage: 'Applied Gift Cards' })}:
            </span>
          </div>
          {cartData?.payments?.map(({ giftcardNumber, amountPlanned }, index) => (
            <div key={'applied-card-' + index} className="my-10 rounded bg-[#f7f8f9] px-14 py-7">
              <div className="w-full">
                <p className="text-14 font-light">{giftcardNumber}</p>
                <p className="relative mt-8 text-14 font-medium">
                  {amountPlanned && CurrencyHelpers.formatForCurrency(amountPlanned, locale)}
                </p>
              </div>
            </div>
          ))}
        </>
      );
    } else if (selectedPaymentMethod === PaymentType.V12) {
      return (
        <div>
          <span className="mr-10 text-14 font-light">Selected product:</span>
          <span className="mr-10 text-14 font-light">{v12PaymentData?.productName}</span>
        </div>
      );
    }
  };

  const { label, content } = useMemo(() => getPaymentDetails(), [type, paymentState]);
  const additionalPaymentlabel = useMemo(() => getAdditionalPaymentDetails(), [type, paymentState, v12PaymentData]);

  return (
    <Preview>
      <div className={`border border-neutral-400 p-16 md:px-20 md:py-24`}>
        <div className="flex items-center justify-between border-l-4 border-ag_color-text_green py-18 pl-18 ">
          <div>{label}</div>
          {content}
        </div>
        {additionalPaymentlabel && (
          <div className="flex items-center justify-between border-l-4 border-ag_color-text_green py-18 pl-18 ">
            <div>{additionalPaymentlabel}</div>
          </div>
        )}
      </div>
    </Preview>
  );
};

export default PaymentPreview;
